import { useEffect, useMemo, useState } from 'react';
import { Breadcrumbs, ContentLayout } from 'components/Layout';
import { FiSearch, FiFilter, FiGrid, FiList, FiTag } from 'react-icons/fi';
import { ForbiddenFallback } from 'components/Elements';
import { Authorization, ROLES } from 'lib/authorization';
import { VaultsList } from '../component/VaultsList';
import { UnstructuredDataPagination } from 'components/Elements';
import { VaultFiltersAnimated } from '../component/VaultFiltersAnimated';
import { Spinner } from 'components/Elements';
import { VaultDetailsDrawer } from '../component/VaultDetailsDrawer';

const DISPLAY_MODES = {
  GRID: 'grid',
  LIST_CLEAN: 'list-clean',
  LIST_COMPACT: 'list-compact',
};

const StatusBadge = ({ status }) => {
  const map = {
    active: {
      label: 'Active',
      cls: 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30',
    },
    frozen: {
      label: 'Frozen',
      cls: 'bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-500/30',
    },
    scheduled_closure: {
      label: 'Scheduled Closure',
      cls: 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30',
    },
    closed: {
      label: 'Closed',
      cls: 'bg-rose-500/15 text-rose-300 ring-1 ring-rose-500/30',
    },
  };
  const m = map[status] || {
    label: status || 'Unknown',
    cls: 'bg-white/10 text-white/80 ring-1 ring-white/20',
  };
  return (
    <span
      className={`inline-flex text-nowrap items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${m.cls}`}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-80" />
      {m.label}
    </span>
  );
};

export const UsersVaults = () => {
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [displayMode, setDisplayMode] = useState(DISPLAY_MODES.GRID);
  const [pageNumber, setPageNumber] = useState(1);
  const [filters, setFilters] = useState({
    alias: '',
    user: '',
    currency: '',
    country: '',
    status: '', // new filter slot
  });

  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedVaultId, setSelectedVaultId] = useState(null);
  const [selectedVaultSnapshot, setSelectedVaultSnapshot] = useState(null);

  const handleSearchChange = (e) => {
    setIsSearching(true);
    setSearchText(e.target.value);
    // debounce is nice; keeping simple
    setTimeout(() => setIsSearching(false), 250);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const openDrawer = (vault) => {
    setSelectedVaultId(vault?.id);
    setSelectedVaultSnapshot(vault || null);
    setDrawerOpen(true);
  };

  const filteredProps = useMemo(
    () => ({
      searchText,
      filters,
      pageNumber,
      displayMode,
      setTotalPages: () => {},
      setTotalItems: () => {},
      // NEW: let the list notify us when a vault is clicked/previewed
      onVaultClick: openDrawer,
      // OPTIONAL: If your VaultsList supports render props for extra chips/badges:
      renderVaultBadge: (v) => <StatusBadge status={v?.status} />,
      // OPTIONAL: Provide minimal metadata mapping for the drawer quick-peek:
      vaultMeta: ['serviceFlags', 'status', 'currency', 'country', 'balance'],
    }),
    [searchText, filters, pageNumber, displayMode]
  );

  useEffect(() => {
    filteredProps.setTotalPages = setTotalPages;
    filteredProps.setTotalItems = setTotalItems;
  }, [filteredProps]);

  return (
    <Authorization
      allowedRoles={[
        ROLES.ROOT_USER,
        ROLES.TRANSACTION_MANAGER,
        ROLES.TECHNICAL,
        ROLES.CUSTOMER_SUPPORT,
      ]}
      forbiddenFallback={<ForbiddenFallback />}
    >
      <ContentLayout
        title="Users Vaults"
        breadcrumbs={<Breadcrumbs />}
        end={
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Display Mode Toggle */}
            <div className="inline-flex items-center gap-2">
              <FiGrid className="opacity-70 hidden md:inline-block" />
              <select
                value={displayMode}
                onChange={(e) => setDisplayMode(e.target.value)}
                className="rounded-lg bg-white/10 text-white px-3 py-1 text-sm border border-white/20"
              >
                <option value={DISPLAY_MODES.GRID}>Grid</option>
                <option value={DISPLAY_MODES.LIST_CLEAN}>List (Clean)</option>
                <option value={DISPLAY_MODES.LIST_COMPACT}>
                  List (Compact)
                </option>
              </select>
            </div>

            {/* Search Bar */}
            <div className="relative flex items-center">
              <span className="absolute left-2 flex items-center justify-center text-white">
                {isSearching ? <Spinner size="sm" /> : <FiSearch />}
              </span>
              <input
                type="text"
                placeholder="Search by Alias, WireID, Email"
                value={searchText}
                onChange={handleSearchChange}
                className="pl-8 pr-4 w-[16vw] text-sm text-white placeholder-white/60 rounded-3xl bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-white/30"
              />
            </div>
          </div>
        }
      >
        <div className="flex flex-row items-center justify-between">
          <VaultFiltersAnimated filters={filters} setFilters={setFilters} />

          <div className="flex flex-row items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 opacity-70">
              <FiTag /> Status
            </span>
            <StatusBadge status="active" />
            <StatusBadge status="frozen" />
            <StatusBadge status="scheduled_closure" />
            <StatusBadge status="closed" />
          </div>
        </div>

        {/* Vault List */}
        <VaultsList
          {...filteredProps}
          setTotalPages={setTotalPages}
          setTotalItems={setTotalItems}
        />

        {/* Pagination */}
        <UnstructuredDataPagination
          currentPage={pageNumber}
          totalPages={totalPages}
          totalItems={totalItems}
          onPageChange={setPageNumber}
          label="vaults"
        />
      </ContentLayout>

      {/* Drawer (Quick Peek) */}
      <VaultDetailsDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        id={selectedVaultId}
        // You can extend the drawer to accept initial snapshot to avoid skeletons
        // snapshot={selectedVaultSnapshot}
      />
    </Authorization>
  );
};
